import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  AlertTriangle, Info, ShieldAlert, Bell, ChevronRight, X
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: {
    bg: 'bg-red-50', border: 'border-red-200', icon: ShieldAlert,
    iconColor: 'text-red-600', textColor: 'text-red-800', badgeBg: 'bg-red-100 text-red-700',
  },
  warning: {
    bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle,
    iconColor: 'text-amber-500', textColor: 'text-amber-800', badgeBg: 'bg-amber-100 text-amber-700',
  },
  caution: {
    bg: 'bg-blue-50', border: 'border-blue-200', icon: Info,
    iconColor: 'text-blue-500', textColor: 'text-blue-800', badgeBg: 'bg-blue-100 text-blue-700',
  },
  info: {
    bg: 'bg-emerald-50', border: 'border-emerald-200', icon: Info,
    iconColor: 'text-emerald-500', textColor: 'text-emerald-800', badgeBg: 'bg-emerald-100 text-emerald-700',
  },
};

function AlertItem({ alert, onDismiss }) {
  const navigate = useNavigate();
  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 ${config.bg} border ${config.border} rounded-md`}
      data-testid={`alert-${alert.type}`}
    >
      <Icon size={16} className={`${config.iconColor} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs leading-relaxed ${config.textColor}`} data-testid="alert-message">
          {alert.message}
        </p>
        {alert.detail && (
          <p className={`text-[10px] mt-0.5 ${config.textColor} opacity-70 font-mono`} data-testid="alert-detail">
            {alert.detail}
          </p>
        )}
        {alert.actionLabel && alert.actionRoute && (
          <button
            onClick={() => navigate(alert.actionRoute)}
            className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold ${config.textColor} hover:underline`}
            data-testid={`alert-action-${alert.type}`}
          >
            {alert.actionLabel}
            <ChevronRight size={12} />
          </button>
        )}
      </div>
      <button
        onClick={() => onDismiss(alert.type)}
        className={`${config.iconColor} opacity-40 hover:opacity-80 shrink-0`}
        data-testid={`alert-dismiss-${alert.type}`}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function AlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const res = await api.get('/tsp/participant/alerts');
      setAlerts(Array.isArray(res.data) ? res.data : []);
    } catch {} finally { setLoading(false); }
  };

  const handleDismiss = (type) => {
    setDismissed(prev => [...prev, type]);
  };

  const visible = alerts.filter(a => !dismissed.includes(a.type));

  if (loading || visible.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="alerts-panel">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={13} className="text-[#6B7280]" />
        <span className="overline">Alerts</span>
        <span className="text-[10px] font-mono font-medium text-[#1B2A4A] bg-[#F0F1F3] px-1.5 py-0.5 rounded-sm" data-testid="alerts-count">
          {visible.length}
        </span>
      </div>
      {visible.map((alert) => (
        <AlertItem key={alert.type} alert={alert} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
